/**
 * Accueil participant : l'événement, l'ambiance (qui est là), mes prochains
 * rendez-vous, et les raccourcis vers les gestes fréquents.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { appelerApi } from '../reseau.ts';
import { useSession } from '../etat.tsx';
import { useLibelles } from '../i18n.tsx';
import { nomAffiche } from '../composants/FicheParticipant.tsx';
import { Concretisation } from '../composants/Concretisation.tsx';

type Statistiques = { inscrits: number; a_rejoint: number; presents: number };
type RendezVous = {
  id: string;
  debut_le: string;
  duree_min: number;
  statut: string;
  autre_participant_id: string;
  fiche: { prenom: string | null; nom: string | null; entreprise: string | null; fonction: string | null } | null;
};

const heure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });

export function Accueil() {
  const { moi } = useSession();
  const { t } = useLibelles();
  const [stats, setStats] = useState<Statistiques | null>(null);
  const [prochains, setProchains] = useState<RendezVous[]>([]);

  useEffect(() => {
    void appelerApi<Statistiques>('/api/repertoire/statistiques').then((r) => {
      if (r.donnees) setStats(r.donnees);
    });
    void appelerApi<{ rendez_vous: RendezVous[] }>('/api/rendez-vous/').then((r) => {
      if (r.donnees) {
        setProchains(
          r.donnees.rendez_vous.filter((rv) => ['propose', 'confirme'].includes(rv.statut)).slice(0, 3),
        );
      }
    });
  }, []);

  const dateEvenement = moi
    ? new Date(moi.debut_le).toLocaleDateString('fr-BE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    : '';

  return (
    <>
      <section className="accroche">
        <p className="accroche__sur-titre">{dateEvenement}</p>
        <h1 className="accroche__titre">{moi?.evenement_nom ?? 'EventMatch.pro'}</h1>
        <p className="accroche__texte">
          {moi?.lieu_nom}
          {moi?.lieu_ville ? ` · ${moi.lieu_ville}` : ''}
        </p>
      </section>

      {stats && (
        <section className="stats" aria-label="Participation">
          <div className="stats__bloc">
            <span className="stats__nombre">{stats.inscrits}</span>
            <span className="stats__legende">inscrits</span>
          </div>
          <div className="stats__bloc">
            <span className="stats__nombre">{stats.a_rejoint}</span>
            <span className="stats__legende">ont rejoint</span>
          </div>
          <div className="stats__bloc stats__bloc--accent">
            <span className="stats__nombre">{stats.presents}</span>
            <span className="stats__legende">sur place</span>
          </div>
        </section>
      )}

      {/* Après l'événement : la question de concrétisation — invisible tant que
          l'API ne renvoie rien. */}
      <Concretisation />

      {!moi?.profil_complete && (
        <Link to="/profil" className="carte rappel-profil">
          <strong>Complétez votre profil</strong>
          <span>
            Deux minutes suffisent — c'est ce qui permet aux bonnes personnes de vous trouver.
          </span>
        </Link>
      )}

      {prochains.length > 0 && (
        <section className="carte prochains" aria-labelledby="titre-prochains">
          <h2 id="titre-prochains">Vos prochains rendez-vous</h2>
          <ul className="prochains__liste">
            {prochains.map((r) => (
              <li key={r.id}>
                <Link to="/agenda" className="prochains__rdv">
                  <span className="prochains__heure">{heure(r.debut_le)}</span>
                  <span className="prochains__nom">
                    {r.fiche ? nomAffiche({ ...r.fiche }) : 'Participant'}
                  </span>
                  <span className={`etiquette${r.statut === 'confirme' ? ' etiquette--succes' : ''}`}>
                    {r.statut === 'confirme' ? 'Confirmé' : 'Proposé'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <nav className="raccourcis" aria-label="Raccourcis">
        <Link to="/participants" className="carte raccourci">
          <strong>Qui est là</strong>
          <span>Parcourir les participants</span>
        </Link>
        <Link to="/programme" className="carte raccourci">
          <strong>Programme</strong>
          <span>Sessions et ateliers</span>
        </Link>
        <Link to="/profil" className="carte raccourci">
          <strong>Mon QR</strong>
          <span>Échanger ses coordonnées</span>
        </Link>
        <Link to="/profil" className="carte raccourci">
          <strong>Mes contacts</strong>
          <span>Exporter CSV / vCard</span>
        </Link>
      </nav>

      <footer className="pied">
        <p className="pied__sponsor">{t('app.sponsor', 'Offert par Elite Lead')}</p>
        <p className="pied__rgpd">
          {t('rgpd.centre.hebergement', "Vos données sont hébergées dans l'Union européenne.")}
        </p>
      </footer>
    </>
  );
}
